<template lang="pug">
  .page.catalog(ref="page")
    .inner-gap
      .layout.flex
        .aside
          products-side-bar

        .results(ref="results")
          client-only
            pre-approval-buyer-warning
            mov-alert-headup

          .page__shelf
            div
              .default-catalog
                h1.page__title  Innovative new products
              .page__description.paginator
                template(v-if="paginator") Showing&nbsp;
                  span.count {{ records.length }}
                  | &nbsp;over&nbsp;
                  span.total {{ paginator.total }}
                  | &nbsp;{{ paginator.total > 1 ? 'products' : 'product' }}
                template(v-else) -

          .filters-bar
            .chips
              nuxt-link.chip(
                v-for="(i, k) in categories"
                :to="getLink(i)"
                :key="i.id"
                :class="{ active: isActive(i) }")
                span.name.pf-ellipsis(v-text="i.name")

          filters-bar

          .sort-area.tr.mb-2
            el-select.sort.vam(v-model="sort" :placeholder="isVendor ? 'Order by Brand name — A > Z' : 'Order by Popularity'")
              el-option(v-if="!isVendor" value="relevance_score,desc" label="Order by Popularity")
              el-option(v-if="canOrderedByActivatedDate" value="activated_at,desc" label="Order by Newest")
              el-option(value="brand_name,asc" label="Order by Brand name — A > Z")
              el-option(value="brand_name,desc" label="Order by Brand name — Z > A")
              el-option(value="name,asc" label="Order by Product name — A > Z")
              el-option(value="name,desc" label="Order by Product name — Z > A")
              template(v-if="isBuyer && !isPreApprovalBuyer")
                el-option(value="price,asc" label="Order by Price — Lower first")
                el-option(value="price,desc" label="Order by Price — Higher first")

          infinite-fetcher(
            :fetch="fetch"
            :paginator="paginator"
            loading-text="Loading more products...")
            products-grid(:records="records" :loading="loading.main" trackProps="CATALOG")

            empty(
              v-if="!records.length && !loading.main"
              title="No results found"
              type="empty.catalog")
            .ender
</template>

<script>
import { PRODUCT_CARD_TYPES } from '~/settings'

import InfiniteFetcher from '~/components/InfiniteFetcher'
import ProductsSideBar from '~/components/menus/ProductsSideBar'
import FiltersBar from '~/components/menus/FiltersBar'
import MovAlertHeadup from '~/components/MovAlertHeadup'
import ProductsGrid from '~/components/products/ProductsGrid'
import PreApprovalBuyerWarning from '~/components/PreApprovalBuyerWarning'
import ProductCard from '~/components/products/ProductCard'
import { mapGetters } from 'vuex'

export default {
  name: 'Catalogs',

  key (route) {
    return route.fullPath
  },

  components: {
    PreApprovalBuyerWarning,
    MovAlertHeadup,
    InfiniteFetcher,
    ProductsSideBar,
    FiltersBar,
    ProductsGrid,
    ProductCard
  },

  layout: 'catalog',

  data () {
    return {
      PRODUCT_CARD_TYPES,
      records: [],
      paginator: null,
      loading: {}
    }
  },

  fetch () {
    return this.fetch()
  },

  head () {
    return {
      title: 'New Products'
    }
  },

  computed: {
    ...mapGetters({
      isBuyer: 'authentication/isBuyer',
      isVendor: 'authentication/isVendor',
      categories: 'master/productCategories',
      isPreApprovalBuyer: 'authentication/isPreApprovalBuyer',
      headBuyerHasAssignedRegions: 'authentication/headBuyerHasAssignedRegions'
    }),

    canOrderedByActivatedDate () {
      return this.isBuyer || this.headBuyerHasAssignedRegions
    },

    category () {
      const categoryId = parseInt(this.$route.query.category_id, 10)
      return this.$store.getters['master/productCategoryById'](categoryId)
    },

    sort: {
      get () {
        const { sort, direction } = this.$route.query

        if (sort && direction) {
          return `${sort},${direction}`
        }

        return this.getDefaultSortOrder()
      },

      set (value) {
        if (typeof value !== 'string') return

        const [sort, direction] = value.split(',')
        this.updateSortQueryParams(sort, direction)
      }
    }
  },

  watch: {
    '$route.query': {
      handler (newQuery, oldQuery) {
        if (this._inactive || JSON.stringify(newQuery) === JSON.stringify(oldQuery))
          return

        this.$fetch()
      }
    }
  },

  methods: {
    getDefaultSortOrder () {
      if (this.isVendor)
        return 'brand_name,asc'

      if (this.canOrderedByActivatedDate)
        return 'activated_at,desc'

      return 'relevance_score,desc'
    },

    updateSortQueryParams (sort, direction) {
      const query = { ...this.$route.query }

      if (sort) query.sort = sort
      if (direction) query.direction = direction

      this.$router.push({ query }).catch(this.$debug.log)
    },

    isActive (category) {
      return +this.$route.query.category_id === category.id
    },

    getLink (category) {
      return {
        query: {
          ...this.$route.query,
          category_id: this.isActive(category) ? undefined : category.id
        }
      }
    },

    getActiveSorting () {
      let { sort, direction } = this.$route.query

      if (!sort || !direction) {
        [sort, direction] = this.sort.split(',')
      }

      return { sort, direction }
    },

    buildSearchQueryParams (query = {}) {
      const { sort, direction } = this.getActiveSorting()

      return {
        ...this.$route.query,
        ...query,
        sort,
        direction,
        'q[new_product]': true
      }
    },

    trackSearchEvent (response, queryParams) {
      this.$amplitude?.track('NEW_PRODUCTS_SEARCH_FETCH_MORE', {
        keyword: queryParams.term,
        page: queryParams.page,
        brand_id: response.brand?.id || null,
        results_length: this.records.length
      })
    },

    handleSearchSuccess (response, clear, queryParams) {
      this.paginator = this.$paginators.parse(response)

      this.records = clear ? response.results : this.records.concat(response.results)

      this.trackSearchEvent(response, queryParams)
    },

    handleSearchError (error) {
      const statusCode = error.response?.status || 404
      const errorObj = { statusCode }

      if (process.server) {
        this.$nuxt.context.res.statusCode = statusCode
        this.$fetchState.error = errorObj
      } else {
        this.$nuxt.error(errorObj)
      }
    },

    fetch (query = {}, { clear = true } = {}) {
      if (clear)
        this.$set(this.loading, 'main', true)

      const queryParams = this.buildSearchQueryParams(query)

      return this.$api.catalog
      .searchProducts(queryParams)
      .then(response => this.handleSearchSuccess(response, clear, queryParams))
      .catch(error => this.handleSearchError(error))
      .finally(() => this.$set(this.loading, 'main', false))
    }
  }
}
</script>

<style lang="scss" src="~/assets/new-products.scss"></style>
<style lang="scss" scoped>
.paginator {
  .count, .total {
    font-weight: $--font-weight-bold;
    color: $--color-primary;
    margin: 0 .35em;
  }
}
.layout {
  gap: 30px;
  .aside {
    flex: 0 200px;
    background: #f4f6f8;
    @include media(sm-down) {
      display: none;
    }
  }
  .results {
    flex: 1;
    .page__shelf {
      .flex {
        gap: 12px;
        @include media(xs-only) {
          width: 100%;
        }
      }
    }

    .sort {
      flex: 1;
      @include media(sm) {
        flex: 0 auto;
      }
    }
  }
}
.filters-bar {
  margin-bottom: 1em;
  .chips {
    display: flex;
    flex-wrap: wrap;
    .chip {
      background: white;
      font-size: 14px;
      line-height: 1em;
      padding: 6px 10px;
      border-radius: 1000px;
      white-space: nowrap;
      color: $--color-text-regular;
      margin-bottom: 10px;
      &:not(:last-child) {
        margin-right: 10px;
      }
      &.reset {
        background: white;
        border: 1px solid #e9edf1;
      }
      .name {
        max-width: 120px;
      }
      &.active {
        color: white;
        background: $--color-primary;
      }
    }
  }
}
</style>
